import type { PublicVesselGroup, PublicVesselSummary } from '../shared/contracts';
import { routeDiagramPoint, RIVER_MOUTH_S_METERS, RIVER_OUTER_S_METERS } from '../shared/river-model';
import { separateRouteHulls } from '../shared/route-hulls';
import { projectedCorridorProgress } from './scene/vessel-motion';
import { trafficMovements, timeAlignedTowMembers } from './tow-groups';

/** Separate the drawn sprites along the atlas, leaving reported dots untouched. */
export function layoutVesselDiagram(vessels: PublicVesselSummary[], groups: PublicVesselGroup[]): Map<string, PublicVesselSummary> {
  const preferred = new Map(vessels.map((v) => [v.id, v]));
  const grouped = new Set<string>();
  for (const movement of trafficMovements(vessels, groups)) {
    if (movement.kind !== 'likely_tow') continue;
    for (const [id, vessel] of timeAlignedTowMembers(movement)) { preferred.set(id, vessel); grouped.add(id); }
  }
  const positions = separateRouteHulls([...vessels].sort((a,b) => b.observedAt.localeCompare(a.observedAt) || a.id.localeCompare(b.id)).map((vessel) => {
    const branch = vessel.sMeters < RIVER_MOUTH_S_METERS;
    const outer = vessel.corridorProgress > 0 ? RIVER_MOUTH_S_METERS + (vessel.sMeters - RIVER_MOUTH_S_METERS) / vessel.corridorProgress : vessel.sMeters - 6500;
    return { id: vessel.id, sMeters: preferred.get(vessel.id)!.sMeters,
      minimum: branch ? outer : vessel.sMeters < 0 ? RIVER_MOUTH_S_METERS + 2 : 0,
      maximum: branch ? RIVER_MOUTH_S_METERS - 2 : vessel.sMeters < 0 ? -2 : RIVER_OUTER_S_METERS,
      radius: grouped.has(vessel.id) ? 42 : 54,
      pointAt: (s: number) => routeDiagramPoint(vessel.corridor, s, projectedCorridorProgress(vessel, s)) };
  }), 5);
  return new Map(vessels.map((vessel) => {
    const sMeters = positions.get(vessel.id) ?? vessel.sMeters;
    return [vessel.id, { ...vessel, sMeters, corridorProgress: projectedCorridorProgress(vessel, sMeters) }];
  }));
}

export function diagramEstimatePath(reported: PublicVesselSummary, display: PublicVesselSummary): string {
  return Array.from({ length: 33 }, (_, index) => {
    const ratio = index / 32;
    const s = reported.sMeters + (display.sMeters - reported.sMeters) * ratio;
    const point = routeDiagramPoint(reported.corridor, s, projectedCorridorProgress(reported, s));
    return `${index ? 'L' : 'M'}${point.x} ${point.y}`;
  }).join(' ');
}
